import React, {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Experiment, Variation } from '@justgiving/experiments';
import classNames from 'classnames';

import {
  ActivityTotalsFragment,
  DistanceUnit,
  MeasurementSystem,
  PageTotal,
  PageTotalsDisplayOptions,
  TimeUnit,
} from '../../../types/__generated__';
import { Experiments } from '../../constants/experiments';
import { activityTotalsFragment } from '../../fragments/activityTotalsFragment';
import { useFlags } from '../../hooks/useFlags';
import usePageQuery, {
  UsePageQueryResult,
  buildQuery,
} from '../../hooks/usePageQuery';
import {
  getDistanceUnitForMeasurementSystem,
  getMeasurementSystemFromSettings,
} from '../../utils/measurementSystem';
import { FormattedMeasurement } from '../Formatters/FormattedMeasurement';
import PageSection from '../PageSection/PageSection';
import { ActionLinks } from './ActionLinks/ActionLinks';
import { ExtraMeasurements } from './ExtraMeasurements/ExtraMeasurements';
import { FilterButtons } from './FilterButtons/FilterButtons';
import {
  ActivityFilters,
  ActivityTotal,
  ActivityTotalMeasurementTypes,
  buildFilteredTotals,
  mapMeasurementTypeToActivityTotalMeasurementType,
} from './filters';
import { ProgressBar } from './ProgressBar/ProgressBar';

import styles from './ActivityTotals.scss';

const FRAGMENT = buildQuery(activityTotalsFragment);

const DefaultPrimaryMeasurementType: ActivityTotalMeasurementTypes = 'distance';

export const ActivityTotals: FunctionComponent = () => {
  let [activityFilter, setActivityFilter] = useState(ActivityFilters.all.key);

  // NOTE: This enables switching the primary measurement type in the browser.
  // TODO: This should be removed once design of this component is complete.
  const [
    customPrimaryMeasurementType,
    setCustomPrimaryMeasurementType,
  ] = useState<ActivityTotalMeasurementTypes | undefined>(undefined);

  const { t } = useTranslation('common', { useSuspense: false });

  const { virtualFitnessEventsDebugFeatures } = useFlags();

  const result = usePageQuery<ActivityTotalsFragment>(FRAGMENT, {
    notifyOnNetworkStatusChange: true,
  });

  if (
    !UsePageQueryResult.hasData(result) ||
    isHiddenByDisplayOptions(result.data)
  ) {
    return null;
  }

  const primaryMeasurementTypeToUse =
    customPrimaryMeasurementType ??
    getPrimaryMeasurementType(result.data) ??
    DefaultPrimaryMeasurementType;

  const settings = result.data?.application?.settings;
  const measurementSystem = getMeasurementSystemFromSettings(settings);
  const timelineTotals: PageTotal[] = result.data?.totals?.timeline ?? [];

  if (settings && !settings.showTotaliser) {
    return null;
  }

  const { activityTypes, activityTotalsMap } = buildViewData(
    timelineTotals,
    measurementSystem,
    activityFilter,
    setActivityFilter,
  );

  const allTotal = activityTotalsMap[ActivityFilters.all.key];
  const allPrimaryMeasurement = allTotal?.[primaryMeasurementTypeToUse];

  if (!allPrimaryMeasurement?.value) {
    return null;
  }

  const total = activityTotalsMap[activityFilter];
  const primaryMeasurement = total[primaryMeasurementTypeToUse];

  // B2C_054 - show hide fitness totaliser
  // or added as a non experiment test if successful
  return (
    <Experiment slug={Experiments.b2c_054_show_hide_fitness_totaliser}>
      <Variation name="A">
        <PageSection>
          <div className={styles.box}>
            <h1 className="jg-h4">{t('activity.totals.heading')}</h1>
            <FilterButtons
              activityTypes={activityTypes}
              activityFilter={activityFilter}
              setActivityFilter={setActivityFilter}
            />
            <div
              className={classNames('jg-text--regular-light', styles.summary)}
            >
              <strong>
                <FormattedMeasurement value={primaryMeasurement} />
              </strong>
              {activityFilter === ActivityFilters.all.key &&
                ` ${t('activity.totals.completedOverall')}`}
            </div>
            {virtualFitnessEventsDebugFeatures && (
              <ProgressBar percent={55}></ProgressBar>
            )}
            {virtualFitnessEventsDebugFeatures && (
              <ExtraMeasurements
                total={total}
                primaryMeasurementType={primaryMeasurementTypeToUse}
              />
            )}
            <ActionLinks
              switchPrimaryMeasurementType={() =>
                setCustomPrimaryMeasurementType(
                  getNextPrimaryMeasurementType(primaryMeasurementTypeToUse),
                )
              }
            />
          </div>
        </PageSection>
      </Variation>
    </Experiment>
  );
};

const isHiddenByDisplayOptions = (data: ActivityTotalsFragment): boolean =>
  data.totals?.displayOptions === PageTotalsDisplayOptions.DoNotDisplay;

const buildViewData = (
  timelineTotals: PageTotal[],
  measurementSystem: MeasurementSystem,
  activityFilter: string,
  setActivityFilter: Dispatch<SetStateAction<string>>,
) => {
  if (timelineTotals.length === 0) {
    return {
      activityTypes: [ActivityFilters.all.key],
      activityTotalsMap: {
        [ActivityFilters.all.key]: buildDefaultZeroTotal(
          getDistanceUnitForMeasurementSystem(measurementSystem),
        ),
      },
    };
  } else {
    const {
      activityTotalsMap,
      activityTypes,
      isSingleActivityMode,
    } = buildFilteredTotals(measurementSystem, timelineTotals);

    if (
      isSingleActivityMode &&
      activityFilter === ActivityFilters.all.key &&
      activityTypes.length > 0
    ) {
      // The initial value of activityFilter is "all".
      // However, we remove "all" if there is only one activity type.
      // So, switch the current filter over to that one type.
      setActivityFilter(activityTypes[0]);
    }

    return {
      activityTypes,
      activityTotalsMap,
    };
  }
};

const buildDefaultZeroTotal = (distanceUnit: DistanceUnit): ActivityTotal => ({
  distance: {
    unit: distanceUnit,
    value: 0,
  },
  duration: {
    unit: TimeUnit.Seconds,
    value: 0,
  },
  elevation: {
    unit: distanceUnit,
    value: 0,
  },
});

const getPrimaryMeasurementType = (
  resultData: ActivityTotalsFragment | undefined,
): ActivityTotalMeasurementTypes | undefined =>
  resultData?.totals?.primaryMeasurementType
    ? mapMeasurementTypeToActivityTotalMeasurementType(
        resultData.totals.primaryMeasurementType,
      )
    : undefined;

const measurementTypes: ActivityTotalMeasurementTypes[] = [
  'distance',
  'duration',
  'elevation',
];

const getNextPrimaryMeasurementType = (
  primaryMeasurementType: ActivityTotalMeasurementTypes,
): ActivityTotalMeasurementTypes => {
  const currentIndex = measurementTypes.indexOf(primaryMeasurementType);
  return measurementTypes[(currentIndex + 1) % measurementTypes.length];
};
